import type { BalanceSummary } from '@familywallet/shared';
import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { TotalsTile } from '../routes/DebtsPage';
import { creditDue, dueSoon, overdueCount } from '../lib/installments';
import { useDebts, useRecurring } from '../lib/ledger';
import { formatAmount } from '../lib/money';
import { CreditDueRow, InstallmentRow } from './InstallmentRow';
import { dueOccurrences, RecurringDueRow } from './RecurringRows';
import { BalanceLines } from './Money';
import { Card, ListGroup, SectionLabel } from './ui/card';

/** Net worth: money plus what is owed to us minus what we owe; "≈" when it mixes currencies. */
export function NetWorthCard({ summary }: { summary: BalanceSummary }) {
  const { t, i18n } = useTranslation();
  const { byCurrency, inBase } = summary.netWorth;
  const single = byCurrency.length === 1 ? byCurrency[0]! : null;
  return (
    <Card className="flex flex-col gap-1">
      <h2 className="text-[13px] text-muted">{t('home.netWorth')}</h2>
      <span dir="ltr" className="text-2xl font-semibold tabular-nums">
        {single
          ? formatAmount(single.balance, single.currency, i18n.language)
          : `${inBase.estimated ? '≈ ' : ''}${formatAmount(inBase.amount, inBase.currency, i18n.language)}`}
      </span>
      {byCurrency.length > 1 && (
        <BalanceLines balances={byCurrency} className="flex flex-col text-[13px] text-muted" />
      )}
      {inBase.missingRates.length > 0 && byCurrency.length > 1 && (
        <span className="text-xs text-muted">
          {t('rates.missingForEstimate', { currencies: inBase.missingRates.join(', ') })}
        </span>
      )}
      <span className="text-xs text-muted">{t('home.netWorthHint')}</span>
    </Card>
  );
}

/** Debts on Home: what we owe and what is owed to us (outside people), and the overdue badge. */
export function DebtsSummaryCard() {
  const { t } = useTranslation();
  const debts = useDebts();
  if (!debts.data) return null;
  const overdue = overdueCount(debts.data.items);
  return (
    <Card className="flex flex-col gap-3">
      <Link
        to="/debts"
        className="-m-1 flex min-h-11 items-center gap-2 rounded-xl p-1 focus-visible:outline-2 focus-visible:outline-accent"
      >
        <h2 className="flex-1 text-[15px] font-semibold">{t('home.debtsTitle')}</h2>
        {overdue > 0 && (
          <span className="rounded-full bg-surface-2 px-2.5 py-1 text-xs font-semibold text-expense">
            {t('debts.overdueBadge', { count: overdue })}
          </span>
        )}
        <ChevronRight aria-hidden="true" className="size-5 text-muted rtl:rotate-180" />
      </Link>
      <div className="grid grid-cols-2 gap-2">
        <TotalsTile label={t('debts.weOwe')} totals={debts.data.totals.weOwe} />
        <TotalsTile label={t('debts.owedToUs')} totals={debts.data.totals.owedToUs} />
      </div>
    </Card>
  );
}

/**
 * "Due soon": overdue installments and recurring occurrences, and those due in the next 7 days.
 * Hidden when there is neither an installment plan nor a recurring transaction.
 */
export function DueSoonCard({ today }: { today: string }) {
  const { t } = useTranslation();
  const debts = useDebts();
  const recurring = useRecurring();
  const hasPlans =
    debts.data?.items.some((d) => (d.hasPlan || d.credit?.dueState) && !d.settled) ?? false;
  const hasRecurring = (recurring.data?.items.length ?? 0) > 0;
  if (!hasPlans && !hasRecurring) return null;
  const installments = debts.data ? dueSoon(debts.data.items, today) : [];
  const occurrences = recurring.data ? dueOccurrences(recurring.data.items) : [];
  const credits = debts.data ? creditDue(debts.data.items, today) : [];
  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>{t('home.dueSoon')}</SectionLabel>
      <LateNotice />
      {installments.length === 0 && occurrences.length === 0 && credits.length === 0 ? (
        <p className="px-1 text-sm text-muted">{t('home.nothingDue')}</p>
      ) : (
        <ListGroup aria-label={t('home.dueSoonHint')}>
          {installments.map((d) => (
            <li key={d.id}>
              <InstallmentRow debt={d} today={today} />
            </li>
          ))}
          {credits.map((d) => (
            <li key={`credit-${d.id}`}>
              <CreditDueRow debt={d} today={today} />
            </li>
          ))}
          {occurrences.map(({ template, occurrence }) => (
            <li key={`${template.id}-${occurrence.dueOn}`}>
              <RecurringDueRow template={template} occurrence={occurrence} today={today} />
            </li>
          ))}
        </ListGroup>
      )}
    </section>
  );
}

const LATE_DISMISSED = 'familywallet.lateNoticeDismissed';

/** "N occurrences were recorded late", after the daily job caught up; dismissable per device. */
export function LateNotice() {
  const { t } = useTranslation();
  const recurring = useRecurring();
  const late = recurring.data?.lateRecorded;
  const [dismissed, setDismissed] = useState<string | null>(() => {
    try {
      return localStorage.getItem(LATE_DISMISSED);
    } catch {
      return null;
    }
  });
  if (!late || late.count === 0 || !late.latestAt || dismissed === late.latestAt) return null;
  const dismiss = () => {
    try {
      localStorage.setItem(LATE_DISMISSED, late.latestAt!);
    } catch {
      // Private mode: the notice just comes back next time.
    }
    setDismissed(late.latestAt);
  };
  return (
    <div
      role="status"
      className="flex items-start gap-3 rounded-[20px] border border-line bg-surface p-3 ps-4"
    >
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[13px] font-semibold">
          {t('recurring.lateNotice', { count: late.count })}
        </span>
        <span className="text-xs text-muted">{t('recurring.lateNoticeHint')}</span>
      </span>
      <button
        type="button"
        onClick={dismiss}
        className="min-h-11 shrink-0 rounded-xl px-2 text-[13px] font-semibold text-accent focus-visible:outline-2 focus-visible:outline-accent"
      >
        {t('recurring.dismiss')}
      </button>
    </div>
  );
}
